import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfigService } from 'src/app/app-config.service';
import { Global } from '../../global.constants';
import { Utility } from '../../utility';
import { ConfigurationService } from '../auth/configuration.service';
import { DBkeys } from '../auth/db-keys';
import { EndpointFactory } from '../auth/endpoint-factory.service';

@Injectable({
  providedIn: 'root'
})
export class AccountEndpoint extends EndpointFactory {

  private readonly getUsersUrl: string = 'api/account/GetUsers';
  private readonly getUserByUserNameUrl: string = 'api/account/users/username';
  private readonly getCurrentUserUrl: string = 'api/account/users/me';
  private readonly getCurrentUserPreferencesUrl: string = 'api/account/users/me/preferences';
  private readonly getUnblockUserUrl: string = 'api/account/users/unblock';
  private readonly getRolesUrl: string = 'api/account/roles';
  private readonly getRoleByRoleNameUrl: string = 'api/account/roles/name';
  private readonly getPermissionsUrl: string = 'api/account/GetPermissions';
  private baseUrl: string;
  get usersUrl() { return this.baseUrl + this.getUsersUrl; }
  get userByUserNameUrl() { return this.baseUrl + this.getUserByUserNameUrl; }
  get currentUserUrl() { return this.baseUrl + this.getCurrentUserUrl; }
  get currentUserUpdateUrl() { return this.baseUrl + Global.BASE_USER_ENDPOINT_UPDATE_CURRENT; }
  get currentUserPreferencesUrl() { return this.baseUrl + this.getCurrentUserPreferencesUrl; }
  get unblockUserUrl() { return this.baseUrl + this.getUnblockUserUrl; }
  get rolesUrl() { return this.baseUrl + this.getRolesUrl; }
  get roleByRoleNameUrl() { return this.baseUrl + this.getRoleByRoleNameUrl; }
  get permissionsUrl() { return this.baseUrl + this.getPermissionsUrl; }
  reqHeader = new HttpHeaders({
    'Content-Type': 'application/json'
  });

  constructor(httpClient: HttpClient,
    configurations: ConfigurationService,
    injector: Injector,
    protected appConfig: AppConfigService) {
    super(httpClient, configurations, injector, appConfig);
    this.baseUrl = this.apiUrl;
  }

  UserInfo(): any {
    const userInfo = {
      encryptOrg: Utility.removeQuotes(sessionStorage.getItem(DBkeys.ENC_ORGID)),
      encryptUsr: Utility.removeQuotes(sessionStorage.getItem(DBkeys.ENC_USER))
    };
    return userInfo;
  }

  getUserEndpoint(userId?: string): Observable<any> {
    let endpointUrl = '';
    let obj = {};
    if (userId) {
      endpointUrl = `${this.usersUrl}`;
      const userInfo = this.UserInfo();
      obj = { id: userId, userInfo };
    } else {
      endpointUrl = this.currentUserUrl;
      obj = { id: userId };
    }
    return this.httpClient.post<any>(endpointUrl, obj, { headers: this.reqHeader })
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUserByUserNameEndpoint(userName: string): Observable<any> {
    const obj = { userName };
    return this.httpClient.post<any>(this.userByUserNameUrl, obj, { headers: this.reqHeader })
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUsersEndpoint(): Observable<any> {
    const endpointUrl = this.usersUrl;
    return this.httpClient.get<any>(endpointUrl)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getNewUserEndpoint(userObject: any): Observable<any> {
    return this.httpClient.post<any>(this.usersUrl, JSON.stringify(userObject), this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUpdateUserEndpoint(userObject: any, userId?: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.httpClient.post<any>(this.currentUserUpdateUrl, JSON.stringify(userObject), httpOptions)
      .pipe(map((response: any) => response as any),
        catchError(this.handleError));
  }

  getPatchUpdateUserEndpoint(patch: {}, userId?: string): Observable<any>;
  getPatchUpdateUserEndpoint(value: any, op: string, path: string, from?: any, userId?: string): Observable<any>;
  getPatchUpdateUserEndpoint(valueOrPatch: any, opOrUserId?: string, path?: string, from?: any, userId?: string): Observable<any> {
    let endpointUrl: string;
    let patchDocument: {};

    if (path) {
      endpointUrl = userId ? `${this.usersUrl}/${userId}` : this.currentUserUrl;
      patchDocument = from ?
        [{ value: valueOrPatch, path, op: opOrUserId, from }] :
        [{ value: valueOrPatch, path, op: opOrUserId }];
    } else {
      endpointUrl = opOrUserId ? `${this.usersUrl}/${opOrUserId}` : this.currentUserUrl;
      patchDocument = valueOrPatch;
    }

    return this.httpClient.patch<any>(endpointUrl, JSON.stringify(patchDocument), this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUserPreferencesEndpoint(): Observable<any> {
    return this.httpClient.get<any>(this.currentUserPreferencesUrl)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUpdateUserPreferencesEndpoint(configuration: string): Observable<any> {
    return this.httpClient.put<any>(this.currentUserPreferencesUrl, JSON.stringify(configuration), this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUnblockUserEndpoint(userId: string): Observable<any> {
    const endpointUrl = `${this.unblockUserUrl}/${userId}`;

    return this.httpClient.put<any>(endpointUrl, null, this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getDeleteUserEndpoint(userId: string): Observable<any> {
    const endpointUrl = `${this.usersUrl}/${userId}`;

    return this.httpClient.delete<any>(endpointUrl, this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getRoleByRoleNameEndpoint(roleName: string): Observable<any> {
    const endpointUrl = `${this.roleByRoleNameUrl}/${roleName}`;
    return this.httpClient.get<any>(endpointUrl)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getRolesEndpoint(page?: number, pageSize?: number): Observable<any> {
    const obj = { page, pageSize };
    return this.httpClient.post<any>(this.rolesUrl, obj, { headers: this.reqHeader })
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getNewRoleEndpoint(roleObject: any): Observable<any> {
    return this.httpClient.post<any>(this.rolesUrl, JSON.stringify(roleObject), this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getUpdateRoleEndpoint(roleObject: any, roleId: string): Observable<any> {
    const endpointUrl = `${this.rolesUrl}/${roleId}`;
    return this.httpClient.put<any>(endpointUrl, JSON.stringify(roleObject), this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getDeleteRoleEndpoint(roleId: string): Observable<any> {
    const endpointUrl = `${this.rolesUrl}/${roleId}`;
    return this.httpClient.delete<any>(endpointUrl, this.httpOptions)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  getPermissionsEndpoint(userId: number, orgId: number): Observable<any> {
    const url = this.baseUrl + Global.BASE_PERMISSIONS_URL;
    const obj = { userId, orgId };
    return this.httpClient.post<any>(url, obj, { headers: this.reqHeader })
      .pipe(map((response: any) => response as any));
  }

  public handleError(error: Response) {
    const response: any = {
      success: false,
      message: Global.DisplayErrorMessage
    };
    return throwError(response);
  }
}
